import React, { useState, useEffect, useMemo } from 'react';
import { FaRegLightbulb, FaChartLine, FaChartPie, FaInfoCircle, FaCalendarAlt, FaLightbulb, FaSpinner } from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { format, subMonths, differenceInMonths, startOfMonth, endOfMonth } from 'date-fns';
import { CURRENCY_SYMBOLS } from '../../utils/currencySymbols';
import { motion } from 'framer-motion';
import DateRangeSelector from './DateRangeSelector';
import ReportModal from './ReportModal';
import { toast } from 'react-toastify';

const SmartInsight = ({ selectedCurrency = 'GBP' }) => {
  const { currentUser } = useAuth();
  const [expenseData, setExpenseData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: format(subMonths(new Date(), 12), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
    label: 'Past 12 Months'
  });
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [generateProgress, setGenerateProgress] = useState(0);
  
  // Get the currency symbol for the selected currency
  const currencySymbol = CURRENCY_SYMBOLS[selectedCurrency] || '£';

  // Fetch expense data from Firestore
  useEffect(() => {
    const fetchExpenseData = async () => {
      if (!currentUser) return;
      
      try {
        setIsLoading(true);
        setError(null);
        
        const expensesRef = doc(db, 'expenses', currentUser.uid);
        const expensesDoc = await getDoc(expensesRef);
        
        let allExpenses = [];
        
        if (expensesDoc.exists()) {
          const userData = expensesDoc.data();
          if (userData && userData.items) {
            // Convert the object structure to array of expenses with dates
            Object.entries(userData.items).forEach(([date, dayExpenses]) => {
              // Only include expenses within the selected date range
              if (date >= dateRange.start && date <= dateRange.end) {
                dayExpenses.forEach(expense => {
                  allExpenses.push({
                    ...expense,
                    date: date
                  });
                });
              }
            });
          }
        }
        
        setExpenseData(allExpenses);
      } catch (error) {
        console.error('Error fetching expense data:', error);
        setError('Failed to load expense data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchExpenseData();
  }, [currentUser, dateRange]);

  // Calculate insights from the expense data
  const insights = useMemo(() => {
    if (!expenseData.length) return null;

    // Group expenses by category
    const categoryTotals = {};
    const monthlyTotals = {};
    const monthlyCategoryTotals = {}; // Track monthly totals per category
    const currentMonth = format(new Date(), 'yyyy-MM');
    
    expenseData.forEach(expense => {
      if (expense.currency !== selectedCurrency) return;
      
      const amount = parseFloat(expense.amount);
      if (isNaN(amount)) return;
      
      // Calculate category totals
      if (!categoryTotals[expense.category]) {
        categoryTotals[expense.category] = 0;
      }
      categoryTotals[expense.category] += amount;
      
      // Calculate monthly totals
      const month = expense.date.substring(0, 7); // yyyy-MM
      if (!monthlyTotals[month]) {
        monthlyTotals[month] = 0;
      }
      monthlyTotals[month] += amount;
      
      // Calculate monthly totals per category
      if (!monthlyCategoryTotals[expense.category]) {
        monthlyCategoryTotals[expense.category] = {};
      }
      if (!monthlyCategoryTotals[expense.category][month]) {
        monthlyCategoryTotals[expense.category][month] = 0;
      }
      monthlyCategoryTotals[expense.category][month] += amount;
    });

    // Find highest spending category
    const highestCategory = Object.entries(categoryTotals)
      .sort(([, a], [, b]) => b - a)[0];

    // Calculate average monthly spending - grouped by complete months
    const completeMonths = Object.entries(monthlyTotals)
      .filter(([month]) => {
        // Exclude current month when calculating average
        if (month === currentMonth) return false;
        
        // Make sure we're only including complete months within the date range
        const monthDate = new Date(month + '-01'); // Convert to date object
        return monthDate >= new Date(startOfMonth(new Date(dateRange.start))) && 
               monthDate <= new Date(endOfMonth(subMonths(new Date(), 1)));
      })
      .map(([, amount]) => amount);
      
    const avgMonthlySpend = completeMonths.length > 0 
      ? completeMonths.reduce((a, b) => a + b, 0) / completeMonths.length 
      : 0;

    // Calculate month-over-month change
    const months = Object.keys(monthlyTotals).sort();
    const currentMonthTotal = monthlyTotals[currentMonth] || 0;
    const lastMonth = months[months.length - 2];
    const lastMonthTotal = monthlyTotals[lastMonth] || 0;
    const monthOverMonthChange = lastMonthTotal ? 
      ((currentMonthTotal - lastMonthTotal) / lastMonthTotal) * 100 : 0;

    // Calculate category-specific month-over-month change
    const highestCatCurrentMonthTotal = monthlyCategoryTotals[highestCategory[0]]?.[currentMonth] || 0;
    const highestCatLastMonthTotal = monthlyCategoryTotals[highestCategory[0]]?.[lastMonth] || 0;
    const highestCatMonthChange = highestCatLastMonthTotal ? 
      ((highestCatCurrentMonthTotal - highestCatLastMonthTotal) / highestCatLastMonthTotal) * 100 : 0;

    // Find potential savings (categories with highest variance)
    const categoryVariances = Object.entries(categoryTotals).map(([category, total]) => {
      const monthlyCategoryTotals = {};
      expenseData.forEach(expense => {
        if (expense.category === category && expense.currency === selectedCurrency) {
          const month = expense.date.substring(0, 7);
          if (!monthlyCategoryTotals[month]) {
            monthlyCategoryTotals[month] = 0;
          }
          monthlyCategoryTotals[month] += parseFloat(expense.amount);
        }
      });
      
      const values = Object.values(monthlyCategoryTotals);
      const monthCount = values.length || 1; // Use actual month count, minimum 1 to avoid division by zero
      const avg = values.reduce((a, b) => a + b, 0) / monthCount;
      const variance = values.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / monthCount;
      
      return { category, variance, total, monthCount };
    });

    const highestVarianceCategory = categoryVariances
      .sort((a, b) => b.variance - a.variance)[0];

    return {
      highestCategory,
      avgMonthlySpend,
      monthOverMonthChange,
      highestCatMonthChange,
      highestVarianceCategory,
      totalSpent: Object.values(categoryTotals).reduce((a, b) => a + b, 0),
      dataPeriod: {
        start: format(new Date(dateRange.start), 'MMM yyyy'),
        end: format(new Date(dateRange.end), 'MMM yyyy')
      },
      categoryTotals,
      categoryVariances,
      monthlyTotals,
      monthlyCategoryTotals
    };
  }, [expenseData, selectedCurrency, dateRange]);

  const handleDateRangeChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  // Generate detailed financial report
  const generateDetailedReport = () => {
    if (!insights) return;
    
    setIsGeneratingReport(true);
    setGenerateProgress(0);
    
    // Simulate generation process with progress updates
    let progress = 0;
    const progressInterval = setInterval(() => {
      progress += 5;
      setGenerateProgress(progress);
      
      if (progress >= 100) {
        clearInterval(progressInterval);
        
        // Prepare the report data
        // Prepare sorted categories for breakdown
        const categoryBreakdown = Object.entries(insights.categoryTotals)
          .map(([name, amount]) => ({
            name,
            amount: parseFloat(amount.toFixed(2)),
            percentage: parseFloat(((amount / insights.totalSpent) * 100).toFixed(2))
          }))
          .sort((a, b) => b.amount - a.amount);
        
        // Prepare the variance data
        const monthCount = insights.highestVarianceCategory.monthCount || 1;
        const highestVariancePct = parseFloat((
          Math.sqrt(insights.highestVarianceCategory.variance) / 
          (insights.highestVarianceCategory.total / monthCount) * 100
        ).toFixed(2));
        
        // Generate recommendations based on the data
        const recommendations = [
          {
            title: `Optimise ${insights.highestCategory[0]} Spending`,
            description: `This category represents ${parseFloat(((insights.highestCategory[1] / insights.totalSpent) * 100).toFixed(2))}% of your total budget. Consider reviewing your ${insights.highestCategory[0].toLowerCase()} expenses to identify potential savings opportunities.`
          },
          {
            title: `Stabilise ${insights.highestVarianceCategory.category} Expenses`,
            description: `Your spending in this category varies by up to ${highestVariancePct}% between months. Creating a more consistent budget could help you manage these expenses better.`
          },
          {
            title: 'Create an Emergency Fund',
            description: `Based on your average monthly expenses of ${currencySymbol}${parseFloat(insights.avgMonthlySpend).toFixed(2)}, aim to save ${currencySymbol}${parseFloat((insights.avgMonthlySpend * 3).toFixed(2))} to ${currencySymbol}${parseFloat((insights.avgMonthlySpend * 6).toFixed(2))} for emergencies.`
          },
        ];
        
        // Add conditional recommendations
        if (insights.monthOverMonthChange > 10) {
          recommendations.push({
            title: 'Address Recent Spending Increase',
            description: `Your spending has increased by ${parseFloat(insights.monthOverMonthChange).toFixed(2)}% compared to last month. Review recent transactions to identify and address this trend.`
          });
        }
        
        // Financial goals suggestions
        const goals = [
          `Save ${currencySymbol}${parseFloat((insights.avgMonthlySpend * 0.2).toFixed(2))} monthly (20% of your income) for long-term goals`,
          `Reduce ${insights.highestCategory[0]} expenses by 15% (saving approximately ${currencySymbol}${parseFloat((insights.highestCategory[1] * 0.15 / 12).toFixed(2))} monthly)`,
          `Build an emergency fund of ${currencySymbol}${parseFloat((insights.avgMonthlySpend * 6).toFixed(2))} (6 months of expenses)`,
          `Create a separate savings account for irregular ${insights.highestVarianceCategory.category} expenses`,
          `Review and optimise subscriptions and recurring charges`
        ];
        
        // Prepare the full report data
        const reportData = {
          period: insights.dataPeriod,
          totalSpent: parseFloat(insights.totalSpent.toFixed(2)),
          avgMonthlySpend: parseFloat(insights.avgMonthlySpend.toFixed(2)),
          monthOverMonthChange: parseFloat(insights.monthOverMonthChange.toFixed(2)),
          currencySymbol,
          highestCategory: {
            name: insights.highestCategory[0],
            amount: parseFloat(insights.highestCategory[1].toFixed(2)),
            percentage: parseFloat(((insights.highestCategory[1] / insights.totalSpent) * 100).toFixed(2))
          },
          highestVarianceCategory: {
            name: insights.highestVarianceCategory.category,
            variance: highestVariancePct,
            amount: parseFloat(insights.highestVarianceCategory.total.toFixed(2))
          },
          categoryBreakdown,
          recommendations,
          goals
        };
        
        // Set the report data and open the modal
        setReportData(reportData);
        setIsGeneratingReport(false);
        setIsReportModalOpen(true);
        toast.success('Report generated successfully!');
      }
    }, 100);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8 text-red-500">
        <FaInfoCircle className="mx-auto h-12 w-12 mb-4" />
        <p>{error}</p>
      </div>
    );
  }

  if (!expenseData.length) {
    return (
      <div className="text-center p-8 text-gray-500">
        <FaInfoCircle className="mx-auto h-12 w-12 mb-4" />
        <p>No expense data available for analysis</p>
      </div>
    );
  }

  const currentMonth = format(new Date(), 'yyyy-MM');

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center">
          <FaLightbulb className="mr-2 text-indigo-600 dark:text-indigo-400" />
          Smart Financial Insights
        </h3>
        <div className="flex items-center gap-4">
          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <FaCalendarAlt className="mr-2" />
            <span>Data from {insights.dataPeriod.start} to {insights.dataPeriod.end}</span>
          </div>
          <DateRangeSelector 
            onDateRangeChange={handleDateRangeChange} 
            currentRange={dateRange}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-sm border-l-4 border-indigo-500 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400 mr-3">
              <FaChartPie className="w-5 h-5" />
            </div>
            <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Savings Potential</h4>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Your current {insights.highestVarianceCategory.category} spending is {currencySymbol}{parseFloat(insights.highestVarianceCategory.total).toFixed(2)} in total ({currencySymbol}{parseFloat((insights.highestVarianceCategory.total/insights.highestVarianceCategory.monthCount).toFixed(2))} monthly). You could save {currencySymbol}{parseFloat((insights.highestVarianceCategory.total * 0.2/insights.highestVarianceCategory.monthCount).toFixed(2))} per month (about {parseFloat(((insights.highestVarianceCategory.total * 0.2/insights.highestVarianceCategory.monthCount / insights.avgMonthlySpend) * 100).toFixed(2))}% of your monthly average spending) for more stable finances.
          </p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-sm border-l-4 border-yellow-500 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400 mr-3">
              <FaChartLine className="w-5 h-5" />
            </div>
            <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Spending Trend</h4>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Your {insights.highestCategory[0]} expenses are {parseFloat(Math.abs(insights.highestCatMonthChange).toFixed(2))}% {insights.highestCatMonthChange > 0 ? 'higher' : 'lower'} than last month.
          </p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-sm border-l-4 border-green-500 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400 mr-3">
              <FaChartLine className="w-5 h-5" />
            </div>
            <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Monthly Overview</h4>
          </div>
          <div className="space-y-3">
            <div className="flex flex-col mb-3">
              <div className="flex justify-between items-center text-sm mb-1">
                <div className="flex flex-col">
                  <span className="text-gray-600 dark:text-gray-300 font-medium">Average monthly</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Historical average from {
                      format(startOfMonth(new Date(dateRange.start)), 'dd MMM yyyy')
                    } to {
                      format(new Date(), 'yyyy-MM') === currentMonth 
                      ? format(endOfMonth(subMonths(new Date(), 1)), 'dd MMM yyyy') 
                      : format(endOfMonth(new Date(dateRange.end)), 'dd MMM yyyy')
                    }
                  </span>
                </div>
                <span className="font-medium">{currencySymbol}{parseFloat(insights.avgMonthlySpend).toFixed(2)}</span>
              </div>
            </div>
            
            <div className="flex flex-col">
              <div className="flex justify-between items-center text-sm mb-1">
                <div className="flex flex-col">
                  <span className="text-gray-600 dark:text-gray-300 font-medium">This month</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    From {format(startOfMonth(new Date()), 'dd MMM yyyy')} to {format(new Date(), 'dd MMM yyyy')} (today)
                  </span>
                </div>
                <span className="font-medium">{currencySymbol}{(insights.monthlyTotals[currentMonth] || 0).toFixed(2)}</span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-900 rounded-full h-2 mt-1">
                <div 
                  className={`h-2 rounded-full ${(insights.monthlyTotals[currentMonth] / insights.avgMonthlySpend) > 1 ? 'bg-amber-500' : 'bg-green-600'}`}
                  style={{ 
                    width: `${Math.min(100, (insights.monthlyTotals[currentMonth] / insights.avgMonthlySpend) * 100)}%` 
                  }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                <span>
                  {parseFloat(((insights.monthlyTotals[currentMonth] || 0) / insights.avgMonthlySpend) * 100).toFixed(2)}% of monthly average
                </span>
                {(insights.monthlyTotals[currentMonth] / insights.avgMonthlySpend) > 1 && (
                  <span className="text-amber-600 dark:text-amber-400">
                    Exceeding average by {parseFloat((((insights.monthlyTotals[currentMonth] || 0) / insights.avgMonthlySpend) - 1) * 100).toFixed(2)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-700 p-6 rounded-xl shadow-sm border-l-4 border-purple-500 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center mb-4">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 mr-3">
              <FaChartPie className="w-5 h-5" />
            </div>
            <h4 className="text-base font-medium text-gray-900 dark:text-gray-100">Spending Distribution</h4>
          </div>
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Your {insights.highestCategory[0]} expenses account for {parseFloat(((insights.highestCategory[1] / insights.totalSpent) * 100).toFixed(2))}% of your total spending.
          </p>
        </motion.div>
      </div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-gradient-to-r from-indigo-500 to-purple-600 p-6 rounded-xl shadow-md text-white hover:shadow-lg transition-shadow mt-6"
      >
        <div className="flex items-center mb-4">
          <div className="p-2 rounded-lg bg-white/20 mr-3">
            <FaRegLightbulb className="w-5 h-5" />
          </div>
          <h4 className="text-lg font-medium">Smart Financial Assistant</h4>
        </div>
        <p className="mb-4">
          Based on your financial history and spending patterns, here are personalised recommendations to improve your financial health:
        </p>
        <ul className="list-disc list-inside space-y-2 mb-4">
          <li>Consider setting up automatic transfers to savings to increase your savings rate</li>
          <li>Review your {insights.highestCategory[0]} expenses for potential optimisation</li>
          <li>Track your spending in {insights.highestVarianceCategory.category} more closely to identify patterns</li>
        </ul>
        <div className="flex justify-center items-center space-y-4 pt-2">
          <button 
            onClick={generateDetailedReport}
            disabled={isGeneratingReport}
            className={`flex items-center space-x-2 px-6 py-3 text-indigo-600 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed
              ${isGeneratingReport ? 'bg-white' : 'bg-gradient-to-r from-white via-gray-50 to-white background-animate hover:scale-105'}`}
            style={{
              backgroundSize: '200% 200%',
              animation: 'gradient-shift 5s ease infinite'
            }}
          >
            {isGeneratingReport ? (
              <>
                {isGeneratingReport && (
                  <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden mx-auto">
                    <div 
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-200"
                      style={{ width: `${generateProgress}%` }}
                    />
                  </div>
                )}
              </>
            ) : (
              <span>Generate Detailed Report</span>
            )}
          </button>
          
        </div>
      </motion.div>
      
      {/* Report Modal */}
      <ReportModal 
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        reportData={reportData}
      />
    </div>
  );
};

export default SmartInsight;